import { AsyncPipe, NgClass } from '@angular/common';
import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import { BehaviorSubject, delay, map, of } from 'rxjs';
import { ScreenSizeService, ViewMode } from '../../../services/screen-size';
import { sortFilterItems } from '../../../shared/utils/sort-fillter-items.util';
import { TruncatePipe } from '../../../shared/pipes/truncate/truncate.pipe';
import { clicked, isDisabled } from '../../checkbox-helper-functions/checkbox-helper-functions';
import { FilterSelectItem, trackByItemKey } from './filterSelectItem';

// This Component implements the Aria Listbox Patter with checkboxes
// https://www.w3.org/TR/2017/WD-wai-aria-practices-1.1-20170628/examples/listbox/listbox.html
@Component({
  selector: 'now-wbsuche-lib-checkbox-liste',
  styleUrls: ['./checkbox-liste.component.scss'],
  templateUrl: './checkbox-liste.component.html',
  imports: [NgClass, AsyncPipe, TruncatePipe]
})
export class CheckboxListeComponent implements AfterViewChecked {
  private readonly ref = inject(ElementRef);
  private readonly screenSizeService = inject(ScreenSizeService);

  private static readonly CHUNK_SIZE = 150;
  private static readonly SCROLL_OPTIONS: ScrollIntoViewOptions = {
    behavior: 'smooth',
    block: 'nearest'
  };

  @Input()
  set filterList(filterList: FilterSelectItem[]) {
    this.observerForItemsAppending.disconnect();
    if (this.pinSelectedAtTop) {
      this.sortedFilterList = sortFilterItems(filterList);
    }
    this.sortedFilterList = [...filterList];
  }

  @Input()
  komponentenId!: string;

  @Input()
  truncate: boolean = false;

  @Input()
  label!: string;

  @Input()
  searchable = false;

  @Input()
  pinSelectedAtTop = false;

  @Input()
  forceTextWrap: boolean = false;

  @Input()
  maxHeight: number | 'none' = 'none';

  @Output()
  checkboxClicked = new EventEmitter<FilterSelectItem>();

  @Output()
  selectedItemVisible = new EventEmitter<boolean>();

  @ViewChildren('listItem', { read: ElementRef })
  private readonly listItems!: QueryList<ElementRef<HTMLElement>>;

  private lastClickTime = new Date();
  private readonly _offset$ = new BehaviorSubject(0);

  public isPhablet$ = this.screenSizeService.rangeTo$(ViewMode.PHABLET);
  public isTablet$ = this.screenSizeService.rangeTo$(ViewMode.TABLET);
  public sortedFilterList: FilterSelectItem[] = [];
  public focusedIndex = -1;
  public readonly isDisabled = isDisabled;
  public readonly clicked = clicked;
  public readonly trackByItemKey = trackByItemKey;

  @HostListener('focusout')
  focusout(): void {
    this.focusedIndex = -1;
  }

  @HostListener('keydown', ['$event'])
  keydown(event: KeyboardEvent): void {
    // Prevent Page scrolling when pressing space Key
    if (event.code == 'Space' || event.key === ' ') {
      event.preventDefault();
    }
  }

  ngAfterViewChecked(): void {
    this.observeChunkTriggerElements();
    this.observeLastSelectedItem();
  }

  private observeLastSelectedItem(): void {
    const lastSelectedItemIndex = this.sortedFilterList.findLastIndex((item) => item.selected);
    const lastSelectedElement = this.listItems.get(lastSelectedItemIndex);
    if (lastSelectedElement) {
      this.observerSelectedItems.observe(lastSelectedElement.nativeElement);
    }
  }

  private observeChunkTriggerElements(): void {
    const firstElement = this.listItems.get(0);
    if (firstElement) {
      this.observerForItemsAppending.observe(firstElement.nativeElement);
    }

    const elementToTriggerNextChunk = this.ref.nativeElement.querySelector(
      `div[data-index="${this._offset$.getValue() + CheckboxListeComponent.CHUNK_SIZE - 50}"]`
    );
    if (elementToTriggerNextChunk) {
      this.observerForItemsAppending.observe(elementToTriggerNextChunk);
    }
  }

  private readonly observerForItemsAppending = new IntersectionObserver((entries) => {
    const offset = this._offset$.getValue();
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const indexString = (entry.target as HTMLElement)?.dataset.index;
        const index = indexString ? Number.parseInt(indexString) : 0;
        if (index > offset) {
          this._offset$.next(offset + CheckboxListeComponent.CHUNK_SIZE);
        } else if (index < offset) {
          this._offset$.next(offset - CheckboxListeComponent.CHUNK_SIZE);
        }
      }
    });
  });

  private readonly observerSelectedItems = new IntersectionObserver((entries) => {
    if (entries.length > 0) {
      this.selectedItemVisible.emit(entries[entries.length - 1].isIntersecting);
    }
  });

  public determineIndex(i: number) {
    return this._offset$.pipe(
      map((offset) => Math.max(offset - CheckboxListeComponent.CHUNK_SIZE * 3, 0) + i)
    );
  }

  public onListClick() {
    this.lastClickTime = new Date();
  }

  public getIsSelected(index: number): boolean {
    return this.sortedFilterList.some((item, itemIndex) => item.selected && itemIndex === index);
  }

  public focusin(): void {
    this.focusedIndex = this.firstSelectedItemIndex;
    if (this.focusedIndex === -1) {
      this.focusedIndex = this.getNextFocusableItemIndex() ?? 0;
    }
    // Wenn die Zeit zwischen dem focusin und dem click auf das UL-Element
    // kurz ist, dann wurde vermutlich mittels click fokussiert
    // andern Falls wurde vermutlich mitteln Tab-Taste fokussiert
    // und dann soll das ausgewählte Listitem fokussiert weden.
    const lastFocusTime = new Date();
    of(null)
      .pipe(delay(100))
      .subscribe(() => {
        if (this.lastClickTime <= lastFocusTime) {
          this.focusedListItem?.nativeElement.scrollIntoView(CheckboxListeComponent.SCROLL_OPTIONS);
        }
      });
  }

  public moveUp(): void {
    const previousFocusableItemIndex = this.getPreviousFocusableItemIndex();
    if (previousFocusableItemIndex !== null) {
      this.focusedIndex = previousFocusableItemIndex;
      this.focusedListItem?.nativeElement.scrollIntoView(CheckboxListeComponent.SCROLL_OPTIONS);
    }
  }

  public moveDown(): void {
    const nextFocusableIndex = this.getNextFocusableItemIndex();
    if (nextFocusableIndex !== null) {
      this.focusedIndex = nextFocusableIndex;
      this.focusedListItem?.nativeElement.scrollIntoView(CheckboxListeComponent.SCROLL_OPTIONS);
    }
  }

  public onItemClick(index: number) {
    this.focusedIndex = index;
    this.setFocusedItem();
  }

  public setFocusedItem(): void {
    const currentItem = this.sortedFilterList.find((_, index) => index === this.focusedIndex);
    if (currentItem) {
      this.checkboxClicked.emit(currentItem);
    }
  }

  public isLastSelectedElement(itemsList: FilterSelectItem[], currentIndex: number) {
    if (currentIndex == itemsList.length - 1) {
      return itemsList[currentIndex].selected;
    } else {
      return itemsList[currentIndex].selected && !itemsList[currentIndex + 1].selected;
    }
  }

  get entries$() {
    return this._offset$.pipe(
      map((offset) => {
        const from = Math.max(offset - CheckboxListeComponent.CHUNK_SIZE * 3, 0);
        const to = offset + CheckboxListeComponent.CHUNK_SIZE;
        return this.sortedFilterList.length <= CheckboxListeComponent.CHUNK_SIZE
          ? this.sortedFilterList
          : this.sortedFilterList.slice(from, to);
      })
    );
  }

  get firstSelectedItemIndex(): number {
    const firstSelectedItem = this.sortedFilterList.find((item) => item.selected);
    if (firstSelectedItem) {
      return this.sortedFilterList.indexOf(firstSelectedItem);
    }
    return -1;
  }

  get focusedListItem(): ElementRef<HTMLElement> {
    return this.listItems?.find((_, index) => {
      return index === this.focusedIndex;
    })!;
  }

  private getNextFocusableItemIndex(): number | null {
    const nextFocusableItemIndex = this.listItems
      .filter((item) => item.nativeElement.getAttribute('aria-disabled') !== 'true')
      .map((item) => Number.parseInt(item.nativeElement.dataset.index!))
      .find((dataIndex) => {
        return dataIndex > this.focusedIndex;
      });

    return nextFocusableItemIndex ?? null;
  }

  private getPreviousFocusableItemIndex(): number | null {
    const previousFocusableItemIndex = this.listItems
      .filter((item) => item.nativeElement.getAttribute('aria-disabled') !== 'true')
      .map((item) => Number.parseInt(item.nativeElement.dataset.index!))
      .findLast((dataIndex) => {
        return dataIndex < this.focusedIndex;
      });

    return previousFocusableItemIndex ?? null;
  }

  public getItemKeyWithoutWhitespace(item: FilterSelectItem): string {
    return item.key.replaceAll(' ', '_');
  }
}
